// boundedSettle.js — Onda 2 (anti-travamento). Governador de concorrência do
// fan-out do Hermes.
//
// Antes o ramo isolado do fanOutAndMerge fazia `Promise.allSettled(children.map(drive))`:
// um run de 20-30 filhos (o caso "aplicar em 20-30 vagas") disparava 20-30 agent
// loops SIMULTÂNEOS no mesmo renderer — cada um com seu reasoning, seus buffers de
// tool, sua RAM. Sem governador, um fan-out grande sozinho estoura a máquina e
// congela a UI. Este helper limita quantos rodam AO MESMO TEMPO, preservando a
// ordem e o formato `{status,value|reason}` que o allSettled entregava (drop-in).
//
// PURO (só orquestra promises; sem I/O próprio). O `fn` é quem faz o trabalho.

/**
 * Roda `fn(item, i)` para cada item com no máximo `limit` execuções concorrentes.
 * Devolve um array (na ordem dos itens) de resultados settled:
 *   { status: 'fulfilled', value } | { status: 'rejected', reason }
 * Nunca rejeita — espelha Promise.allSettled, só que com teto de concorrência.
 * @template T,R
 * @param {T[]} items
 * @param {number} limit  Máximo concorrente (coagido para ≥1).
 * @param {(item: T, index: number) => Promise<R>} fn
 * @returns {Promise<Array<{status:'fulfilled',value:R}|{status:'rejected',reason:unknown}>>}
 */
export async function settleWithConcurrency(items, limit, fn) {
  const list = Array.isArray(items) ? items : [];
  const results = new Array(list.length);
  if (list.length === 0) return results;
  const cap = Math.max(1, Math.min(Number(limit) || 1, list.length));
  let cursor = 0;
  async function worker() {
    // Cada worker puxa o próximo índice livre até acabar a fila.
    for (;;) {
      const i = cursor;
      cursor += 1;
      if (i >= list.length) return;
      try {
        results[i] = { status: 'fulfilled', value: await fn(list[i], i) };
      } catch (reason) {
        results[i] = { status: 'rejected', reason };
      }
    }
  }
  await Promise.all(Array.from({ length: cap }, () => worker()));
  return results;
}

// Teto default de filhos concorrentes num fan-out isolado. 3 dá vazão real sem
// deixar um fan-out grande abrir 20-30 loops de agente ao mesmo tempo no renderer.
export const FANOUT_MAX_CONCURRENCY = 3;
